import React, { useEffect, useState } from 'react';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { pageTitle } from '@/lib/appConfig';
import {
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
  getApiBaseUrl,
} from '@/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Pencil, Trash2, ClipboardList, Search, X, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ItemRaw {
  id: string;
  name: string;
  sku: string;
  buying_price: number | string;
  selling_price: number | string;
  quantity: number;
  vat_enabled?: number;
  barcode?: string;
  imeis?: string;
  image_path?: string | null;
}
interface Item extends Omit<ItemRaw, 'buying_price' | 'selling_price'> {
  buying_price: number;
  selling_price: number;
  quantity: number;
  barcode?: string;
  imeis?: string;
  vat_enabled?: number;
  image_path?: string | null;
}

const MAX_ITEM_IMAGE_DIMENSION = 900;
const COMPRESSED_ITEM_IMAGE_MIME = 'image/webp';
const COMPRESSED_ITEM_IMAGE_QUALITY = 0.78;

const toItemImageUrl = (imagePath?: string | null) =>
  imagePath ? `${getApiBaseUrl()}/${String(imagePath).replace(/^\/+/, '')}` : null;

function ItemImageThumb({ item, onPreview }: { item: Item; onPreview: (item: Item) => void }) {
  const imageUrl = toItemImageUrl(item.image_path);

  if (!imageUrl) {
    return (
      <div className="h-12 w-12 rounded-md border bg-muted/40 flex items-center justify-center text-[10px] text-muted-foreground shrink-0">
        No image
      </div>
    );
  }

  return (
    <div className="relative h-12 w-12 shrink-0">
      <img
        src={imageUrl}
        alt={item.name}
        className="h-12 w-12 rounded-md object-cover border"
      />
      <button
        type="button"
        onClick={() => onPreview(item)}
        className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
        aria-label={`View image of ${item.name}`}
      >
        <Eye className="h-3 w-3" />
      </button>
    </div>
  );
}

export default function ItemsPage() {
  useEffect(() => {
    document.title = pageTitle('Items');
  }, []);
  const { toast } = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    buying_price: '',
    selling_price: '',
    barcode: '',
    imeis: '',
    hasImei: false,
    quantity: '',
    vat_enabled: true,
  });
  const [selectedImageData, setSelectedImageData] = useState<string | null>(null);
  const [selectedImageName, setSelectedImageName] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewItem, setPreviewItem] = useState<Item | null>(null);
  const currencySymbol = useCurrencySymbol();

  const normalizeItems = (rawItems: ItemRaw[]): Item[] =>
    rawItems.map(i => ({
      ...i,
      buying_price: Number(i.buying_price),
      selling_price: Number(i.selling_price),
      quantity: Number(i.quantity),
      vat_enabled: Number(i.vat_enabled ?? 0),
      barcode: i.barcode || '',
      imeis: i.imeis || '',
      image_path: i.image_path || null,
    }));

  const resetImageState = () => {
    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setSelectedImageData(null);
    setSelectedImageName(null);
    setImagePreviewUrl(null);
    setRemoveCurrentImage(false);
  };

  const loadImageElement = (source: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = source;
    });

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('Failed to read image'));
      };
      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(file);
    });

  const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality: number) =>
    new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Failed to compress image'));
      }, mimeType, quality);
    });

  const compressImage = async (file: File) => {
    const source = await readFileAsDataUrl(file);
    const image = await loadImageElement(source);
    const longestSide = Math.max(image.width, image.height);
    const scale = longestSide > MAX_ITEM_IMAGE_DIMENSION ? MAX_ITEM_IMAGE_DIMENSION / longestSide : 1;
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Image compression is not supported in this browser');
    }

    context.drawImage(image, 0, 0, width, height);
    const compressedBlob = await canvasToBlob(canvas, COMPRESSED_ITEM_IMAGE_MIME, COMPRESSED_ITEM_IMAGE_QUALITY);
    const compressedDataUrl = await readFileAsDataUrl(
      new File([compressedBlob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: COMPRESSED_ITEM_IMAGE_MIME })
    );

    return {
      dataUrl: compressedDataUrl,
      fileName: file.name.replace(/\.[^.]+$/, '') + '.webp',
      previewUrl: URL.createObjectURL(compressedBlob),
      size: compressedBlob.size,
    };
  };

  useEffect(() => () => {
    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
  }, [imagePreviewUrl]);

  useEffect(() => {
    async function loadData() {
      try {
        const itemsRes = await fetchItems();
        setItems(normalizeItems(itemsRes.data as ItemRaw[]));
      } catch (err: any) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    }
    loadData();
  }, [toast]);

  const filtered = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.barcode && item.barcode.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const openDialog = (item?: Item) => {
    resetImageState();
    if (item) {
      setEditingItem(item);
      const parsedImeis = item.imeis ? JSON.parse(item.imeis).join(', ') : '';
      setFormData({
        name: item.name,
        sku: item.sku,
        buying_price: item.buying_price.toString(),
        selling_price: item.selling_price.toString(),
        barcode: item.barcode || '',
        imeis: parsedImeis,
        hasImei: !!item.imeis,
        quantity: item.quantity.toString(),
        vat_enabled: Boolean(item.vat_enabled),
      });
      setImagePreviewUrl(toItemImageUrl(item.image_path));
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        sku: '',
        buying_price: '',
        selling_price: '',
        barcode: '',
        imeis: '',
        hasImei: false,
        quantity: '',
        vat_enabled: true,
      });
    }
    setDialogOpen(true);
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Error', description: 'Please select a valid image file', variant: 'destructive' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Error', description: 'Image size must be 5MB or less', variant: 'destructive' });
      return;
    }

    try {
      const compressed = await compressImage(file);
      if (imagePreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }

      setSelectedImageData(compressed.dataUrl);
      setSelectedImageName(compressed.fileName);
      setImagePreviewUrl(compressed.previewUrl);
      setRemoveCurrentImage(false);
      toast({
        title: 'Image Ready',
        description: `Compressed to ${(compressed.size / 1024).toFixed(0)} KB before upload.`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to compress image',
        variant: 'destructive',
      });
    }

    event.target.value = '';
  };

  const clearSelectedImage = () => {
    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setSelectedImageData(null);
    setSelectedImageName(null);
    setImagePreviewUrl(null);
    setRemoveCurrentImage(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, sku, buying_price, selling_price, barcode, imeis, hasImei, quantity, vat_enabled } = formData;
    if (!name || !sku || !buying_price || !selling_price) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    try {
      const imeisArray = hasImei && imeis ? imeis.split(',').map(s => s.trim()).filter(s => s) : null;
      const effectiveQuantity = hasImei ? 1 : (quantity || 0);
      const payload: Record<string, string | number | boolean | null> = {
        name,
        sku,
        buying_price: parseFloat(buying_price),
        selling_price: parseFloat(selling_price),
        barcode: barcode || null,
        imeis: imeisArray ? JSON.stringify(imeisArray) : null,
        quantity: parseInt(effectiveQuantity.toString(), 10),
        vat_enabled: vat_enabled ? 1 : 0,
      };

      if (selectedImageData) {
        payload.image_data = selectedImageData;
        payload.image_name = selectedImageName;
      }
      if (removeCurrentImage) {
        payload.remove_image = true;
      }

      if (editingItem) {
        await updateItem(editingItem.id, payload);
        toast({ title: 'Updated', description: 'Item updated successfully' });
      } else {
        await createItem(payload);
        toast({ title: 'Created', description: 'Item created successfully' });
      }

      const itemsRes = await fetchItems();
      setItems(normalizeItems(itemsRes.data as ItemRaw[]));
      resetImageState();
      setDialogOpen(false);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteItem(id);
      toast({ title: 'Deleted', description: 'Item deleted successfully' });
      const itemsRes = await fetchItems();
      setItems(normalizeItems(itemsRes.data as ItemRaw[]));
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Items</h1>
        <p className="text-muted-foreground mt-1">Manage your inventory items.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              <CardTitle>Item Management</CardTitle>
            </div>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" /> Add New Item
            </Button>
          </div>
          <CardDescription>
            Add, edit, and manage your items. Quantities reflect current inventory.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items by name, SKU, or barcode..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="max-w-xs"
              />
            </div>

            {/* Desktop / tablet: real table, but cells wrap instead of forcing
                a single line — that's what was pushing the table wider than
                the viewport and causing horizontal scroll. */}
            <div className="hidden md:block border rounded-lg">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[6%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Image</TableHead>
                    <TableHead className="w-[15%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Name</TableHead>
                    <TableHead className="w-[9%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">SKU</TableHead>
                    <TableHead className="w-[10%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Barcode</TableHead>
                    <TableHead className="w-[14%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">IMEIs</TableHead>
                    <TableHead className="w-[6%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Quantity</TableHead>
                    <TableHead className="w-[9%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">VAT</TableHead>
                    <TableHead className="w-[10%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Buying Price</TableHead>
                    <TableHead className="w-[10%] whitespace-normal break-words text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Selling Price</TableHead>
                    <TableHead className="w-[11%] whitespace-normal break-words text-right text-xs lg:text-sm px-2 py-2 lg:px-4 h-auto">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="align-top py-2 px-2 lg:p-4">
                        <ItemImageThumb item={item} onPreview={setPreviewItem} />
                      </TableCell>
                      <TableCell className="align-top whitespace-normal break-words text-xs lg:text-sm py-2 px-2 lg:p-4">{item.name}</TableCell>
                      <TableCell className="align-top whitespace-normal break-words text-xs lg:text-sm py-2 px-2 lg:p-4">{item.sku}</TableCell>
                      <TableCell className="align-top whitespace-normal break-words text-xs lg:text-sm py-2 px-2 lg:p-4">{item.barcode || '—'}</TableCell>
                      <TableCell className="align-top whitespace-normal break-words text-xs lg:text-sm py-2 px-2 lg:p-4">{item.imeis ? JSON.parse(item.imeis).join(', ') : '—'}</TableCell>
                      <TableCell className="align-top whitespace-normal text-xs lg:text-sm py-2 px-2 lg:p-4">{item.quantity}</TableCell>
                      <TableCell className="align-top whitespace-normal text-xs lg:text-sm py-2 px-2 lg:p-4">{item.vat_enabled ? 'Enabled' : 'Disabled'}</TableCell>
                      <TableCell className="align-top whitespace-normal text-xs lg:text-sm py-2 px-2 lg:p-4">{currencySymbol}{item.buying_price.toFixed(2)}</TableCell>
                      <TableCell className="align-top whitespace-normal text-xs lg:text-sm py-2 px-2 lg:p-4">{currencySymbol}{item.selling_price.toFixed(2)}</TableCell>
                      <TableCell className="align-top text-right py-2 px-2 lg:p-4">
                        <div className="flex justify-end gap-1 lg:gap-2">
                          <Button variant="ghost" size="icon" className="h-8 w-8 lg:h-10 lg:w-10" onClick={() => openDialog(item)}>
                            <Pencil className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 lg:h-10 lg:w-10" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: a table can't fit this many fields at any readable
                font size without either scrolling or trimming data, so each
                item becomes a stacked card instead — every field still shows
                in full, just vertically. */}
            <div className="md:hidden space-y-3">
              {filtered.length === 0 ? (
                <div className="border rounded-lg py-8 text-center text-sm text-muted-foreground">
                  No items found.
                </div>
              ) : (
                filtered.map(item => (
                  <div key={item.id} className="border rounded-lg p-3 space-y-3">
                    <div className="flex items-start gap-3">
                      <ItemImageThumb item={item} onPreview={setPreviewItem} />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm break-words">{item.name}</p>
                        <p className="text-xs text-muted-foreground break-words">SKU: {item.sku}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(item)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs border-t pt-3">
                      <div>
                        <p className="text-muted-foreground">Barcode</p>
                        <p className="break-words">{item.barcode || '—'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">IMEIs</p>
                        <p className="break-words">{item.imeis ? JSON.parse(item.imeis).join(', ') : '—'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Quantity</p>
                        <p>{item.quantity}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">VAT</p>
                        <p>{item.vat_enabled ? 'Enabled' : 'Disabled'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Buying Price</p>
                        <p>{currencySymbol}{item.buying_price.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Selling Price</p>
                        <p>{currencySymbol}{item.selling_price.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <Dialog open={!!previewItem} onOpenChange={(open) => !open && setPreviewItem(null)}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{previewItem?.name}</DialogTitle>
                </DialogHeader>
                {previewItem?.image_path && (
                  <img
                    src={toItemImageUrl(previewItem.image_path) || ''}
                    alt={previewItem.name}
                    className="w-full max-h-[70vh] object-contain rounded-md"
                  />
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-w-full max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Edit an existing item.' : 'Add a new item.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                placeholder="Enter item name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">SKU</label>
              <Input
                placeholder="Enter SKU"
                value={formData.sku}
                maxLength={8}
                onChange={e => setFormData({ ...formData, sku: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Barcode</label>
              <Input
                placeholder="Enter barcode (optional)"
                value={formData.barcode}
                onChange={e => setFormData({ ...formData, barcode: e.target.value })}
              />
            </div>
            <div className="space-y-3">
              <label className="text-sm font-medium">Item Image</label>
              <div className="flex items-start gap-4">
                <div className="h-24 w-24 rounded-md border bg-muted/30 overflow-hidden flex items-center justify-center">
                  {imagePreviewUrl ? (
                    <img src={imagePreviewUrl} alt="Item preview" className="h-full w-full object-cover" />
                  ) : (
                    <div className="text-xs text-muted-foreground text-center px-2">No image</div>
                  )}
                </div>
                <div className="space-y-2">
                  <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleImageChange} />
                  <div className="flex gap-2">
                    {imagePreviewUrl && (
                      <Button type="button" variant="outline" size="sm" onClick={clearSelectedImage}>
                        <X className="h-4 w-4 mr-2" /> Remove Image
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">JPEG, PNG, or WEBP up to 5MB.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="has-imei"
                checked={formData.hasImei}
                onCheckedChange={checked => setFormData({
                  ...formData,
                  hasImei: !!checked,
                  imeis: checked ? formData.imeis : '',
                  quantity: checked ? '1' : formData.quantity,
                })}
              />
              <label htmlFor="has-imei" className="text-sm font-medium">Enable IMEI</label>
            </div>
            {formData.hasImei && (
              <div className="space-y-2">
                <label className="text-sm font-medium">IMEIs (comma-separated)</label>
                <Input
                  placeholder="Enter IMEIs, e.g., 123456789012345, 987654321098765"
                  value={formData.imeis}
                  onChange={e => setFormData({ ...formData, imeis: e.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Quantity</label>
              <Input
                type="number"
                placeholder="Enter quantity"
                value={formData.hasImei ? '1' : formData.quantity}
                onChange={e => !formData.hasImei && setFormData({ ...formData, quantity: e.target.value })}
                disabled={formData.hasImei}
                required
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vat-enabled"
                checked={formData.vat_enabled}
                onCheckedChange={checked => setFormData({ ...formData, vat_enabled: !!checked })}
              />
              <label htmlFor="vat-enabled" className="text-sm font-medium">Enable VAT for this item</label>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Buying Price</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter buying price"
                value={formData.buying_price}
                onChange={e => setFormData({ ...formData, buying_price: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Selling Price</label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter selling price"
                value={formData.selling_price}
                onChange={e => setFormData({ ...formData, selling_price: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              {editingItem ? 'Update Item' : 'Save Item'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
